import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  TrophyIcon,
  StarIcon,
  AcademicCapIcon,
  FireIcon,
  UserIcon
} from '@heroicons/react/24/outline';

interface StudentRanking {
  rank: number;
  studentId: string;
  name: string;
  avatar?: string;
  totalPoints: number;
  coursesCompleted: number;
  averageScore: number;
}

const RankingsPage: React.FC = () => {
  const { userProfile } = useAuth();
  const [rankings, setRankings] = useState<StudentRanking[]>([]);
  const [loading, setLoading] = useState(true);
  const [userRank, setUserRank] = useState<StudentRanking | null>(null);

  useEffect(() => {
    fetchRankings();
  }, []);

  const fetchRankings = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/course-progress/rankings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setRankings(result.data);
          // Find current user's rank
          const currentUser = result.data.find((student: StudentRanking) => 
            student.studentId === userProfile?.uid
          );
          setUserRank(currentUser || null);
        }
      }
    } catch (error) {
      console.error('Error fetching rankings:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <TrophyIcon className="h-6 w-6 text-yellow-500" />;
      case 2:
        return <TrophyIcon className="h-6 w-6 text-gray-400" />;
      case 3:
        return <TrophyIcon className="h-6 w-6 text-orange-600" />;
      default:
        return <span className="text-gray-600 font-semibold">#{rank}</span>;
    }
  };

  const getRankBadge = (rank: number) => {
    if (rank <= 3) {
      return `bg-gradient-to-r ${
        rank === 1 ? 'from-yellow-400 to-yellow-600' :
        rank === 2 ? 'from-gray-300 to-gray-500' :
        'from-orange-400 to-orange-600'
      } text-white`;
    }
    return 'bg-white border border-gray-200';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">🏆 Student Rankings</h1>
        <p className="text-lg text-gray-600">
          See how you rank among your peers based on course completion and quiz performance
        </p>
      </div>

      {/* User's Current Rank */}
      {userRank && (
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-6 text-white mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                {userRank.avatar ? (
                  <img
                    src={userRank.avatar}
                    alt={userRank.name}
                    className="h-16 w-16 rounded-full border-4 border-white"
                  />
                ) : (
                  <div className="h-16 w-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                    <UserIcon className="h-8 w-8 text-white" />
                  </div>
                )}
              </div>
              <div className="ml-4">
                <h2 className="text-xl font-bold">Your Current Rank</h2>
                <p className="text-indigo-100">You're doing great! Keep learning to climb higher.</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold">#{userRank.rank}</div>
              <div className="text-sm text-indigo-100">out of {rankings.length} students</div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-indigo-400">
            <div className="text-center">
              <div className="text-2xl font-bold">{userRank.totalPoints}</div>
              <div className="text-sm text-indigo-100">Total Points</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">{userRank.coursesCompleted}</div>
              <div className="text-sm text-indigo-100">Courses Completed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">{userRank.averageScore}%</div>
              <div className="text-sm text-indigo-100">Average Score</div>
            </div>
          </div>
        </div>
      )}

      {/* Top 3 Podium */}
      {rankings.length >= 3 && (
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-6">🥇 Top Performers</h2>
          <div className="flex justify-center items-end space-x-4">
            {/* 2nd Place */}
            <div className="text-center">
              <div className="bg-gradient-to-t from-gray-300 to-gray-500 rounded-lg p-6 mb-4 h-32 flex flex-col justify-end">
                <div className="text-white text-center">
                  <div className="text-2xl font-bold">#2</div>
                  <TrophyIcon className="h-8 w-8 mx-auto mt-2" />
                </div>
              </div>
              <div className="flex flex-col items-center">
                {rankings[1].avatar ? (
                  <img
                    src={rankings[1].avatar}
                    alt={rankings[1].name}
                    className="h-16 w-16 rounded-full border-4 border-gray-300 mb-2"
                  />
                ) : (
                  <div className="h-16 w-16 bg-gray-200 rounded-full flex items-center justify-center mb-2">
                    <UserIcon className="h-8 w-8 text-gray-500" />
                  </div>
                )}
                <div className="font-semibold text-gray-900">{rankings[1].name}</div>
                <div className="text-sm text-gray-600">{rankings[1].totalPoints} points</div>
              </div>
            </div>

            {/* 1st Place */}
            <div className="text-center">
              <div className="bg-gradient-to-t from-yellow-400 to-yellow-600 rounded-lg p-6 mb-4 h-40 flex flex-col justify-end">
                <div className="text-white text-center">
                  <div className="text-3xl font-bold">#1</div>
                  <TrophyIcon className="h-10 w-10 mx-auto mt-2" />
                </div>
              </div>
              <div className="flex flex-col items-center">
                {rankings[0].avatar ? (
                  <img
                    src={rankings[0].avatar}
                    alt={rankings[0].name}
                    className="h-20 w-20 rounded-full border-4 border-yellow-400 mb-2"
                  />
                ) : (
                  <div className="h-20 w-20 bg-yellow-100 rounded-full flex items-center justify-center mb-2">
                    <UserIcon className="h-10 w-10 text-yellow-600" />
                  </div>
                )}
                <div className="font-bold text-gray-900 text-lg">{rankings[0].name}</div>
                <div className="text-sm text-gray-600">{rankings[0].totalPoints} points</div>
                <div className="text-xs text-yellow-600 font-medium">👑 Champion</div>
              </div>
            </div>

            {/* 3rd Place */}
            <div className="text-center">
              <div className="bg-gradient-to-t from-orange-400 to-orange-600 rounded-lg p-6 mb-4 h-28 flex flex-col justify-end">
                <div className="text-white text-center">
                  <div className="text-xl font-bold">#3</div>
                  <TrophyIcon className="h-6 w-6 mx-auto mt-2" />
                </div>
              </div>
              <div className="flex flex-col items-center">
                {rankings[2].avatar ? (
                  <img
                    src={rankings[2].avatar}
                    alt={rankings[2].name}
                    className="h-14 w-14 rounded-full border-4 border-orange-400 mb-2"
                  />
                ) : (
                  <div className="h-14 w-14 bg-orange-100 rounded-full flex items-center justify-center mb-2">
                    <UserIcon className="h-7 w-7 text-orange-600" />
                  </div>
                )}
                <div className="font-semibold text-gray-900">{rankings[2].name}</div>
                <div className="text-sm text-gray-600">{rankings[2].totalPoints} points</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Full Rankings Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Complete Rankings</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rank
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Student
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Points
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Courses Completed
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Average Score
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rankings.map((student) => (
                <tr
                  key={student.studentId}
                  className={`hover:bg-gray-50 ${
                    student.studentId === userProfile?.uid ? 'bg-indigo-50' : ''
                  }`}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`flex items-center justify-center w-10 h-10 rounded-full ${getRankBadge(student.rank)}`}>
                      {getRankIcon(student.rank)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        {student.avatar ? (
                          <img
                            src={student.avatar}
                            alt={student.name}
                            className="h-10 w-10 rounded-full"
                          />
                        ) : (
                          <div className="h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center">
                            <UserIcon className="h-5 w-5 text-gray-500" />
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className={`text-sm font-medium ${
                          student.studentId === userProfile?.uid ? 'text-indigo-900' : 'text-gray-900'
                        }`}>
                          {student.name}
                          {student.studentId === userProfile?.uid && (
                            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                              You
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <FireIcon className="h-4 w-4 text-orange-500 mr-2" />
                      <span className="text-sm font-semibold text-gray-900">
                        {student.totalPoints.toLocaleString()}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <AcademicCapIcon className="h-4 w-4 text-blue-500 mr-2" />
                      <span className="text-sm text-gray-900">{student.coursesCompleted}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <StarIcon className="h-4 w-4 text-yellow-500 mr-2" />
                      <span className="text-sm text-gray-900">{student.averageScore}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Motivational Section */}
      {rankings.length === 0 && (
        <div className="text-center py-12">
          <TrophyIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Rankings Yet</h2>
          <p className="text-gray-600">Complete some courses and take quizzes to appear on the leaderboard!</p>
        </div>
      )}

      {rankings.length > 0 && (
        <div className="mt-8 bg-gradient-to-r from-green-400 to-blue-500 rounded-xl p-6 text-white text-center">
          <h3 className="text-2xl font-bold mb-2">🚀 Keep Learning!</h3>
          <p className="text-green-100">
            Complete more courses and improve your quiz scores to climb the rankings.
            Every course completed and every quiz passed gets you closer to the top!
          </p>
        </div>
      )}
    </div>
  );
};

export default RankingsPage; 